import { Component, ErrorInfo, ReactNode } from 'react';
import {
  Container,
  Paper,
  Box,
  Typography,
  Alert,
  Button,
} from '@mui/material';
import { CloudOff, Refresh, Settings } from '@mui/icons-material';
import { themeColors } from '../../utils/theme';
import { S3ServiceError } from '../../services/S3Service';

interface S3ErrorBoundaryProps {
  children: ReactNode;
  onRetry?: () => void;
  onConfigureS3?: () => void;
}

interface S3ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

/**
 * Specialized Error Boundary for S3-related components
 * 
 * Provides context-aware error handling for S3 operations with
 * specific messaging and recovery options for storage-related issues.
 */
export class S3ErrorBoundary extends Component<S3ErrorBoundaryProps, S3ErrorBoundaryState> {
  constructor(props: S3ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<S3ErrorBoundaryState> {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('S3ErrorBoundary caught an error:', error, errorInfo);
    
    this.setState({
      error,
      errorInfo,
    });

    // Log S3-specific error details if available
    if (error instanceof S3ServiceError) {
      console.error('S3 Service Error Details:', {
        correlationId: error.correlationId,
        errorCategory: error.errorCategory,
        userMessage: error.userMessage,
        technicalMessage: error.technicalMessage,
        isRetryable: error.isRetryable,
        retryAfter: error.retryAfter,
      });
    }
  }

  handleRetry = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });

    if (this.props.onRetry) {
      this.props.onRetry();
    }
  };

  getErrorDetails() {
    const { error } = this.state;
    
    if (error instanceof S3ServiceError) {
      return {
        title: 'Storage Access Error',
        message: error.userMessage || 'Unable to access file storage. Please try again.',
        isRetryable: error.isRetryable,
        showConfigOption: error.errorCategory === 'PermissionDenied' || error.errorCategory === 'InvalidCredentials',
        severity: error.isRetryable ? 'warning' as const : 'error' as const,
      };
    }

    // Handle other types of errors
    if (error?.message?.includes('network') || error?.message?.includes('fetch')) {
      return {
        title: 'Connection Error',
        message: 'Unable to connect to storage service. Please check your internet connection and try again.',
        isRetryable: true,
        showConfigOption: false,
        severity: 'warning' as const,
      };
    }

    // Generic error fallback
    return {
      title: 'File Browser Error',
      message: 'An unexpected error occurred while accessing files. Please try again or contact support.',
      isRetryable: true,
      showConfigOption: false,
      severity: 'error' as const,
    };
  }

  render() {
    const { children, onConfigureS3 } = this.props;
    const { hasError } = this.state;

    if (hasError) {
      const errorDetails = this.getErrorDetails();

      return (
        <Container maxWidth="lg">
          <Box sx={{ mt: 4, mb: 4 }}>
            <Paper
              elevation={3}
              sx={{
                p: 4,
                bgcolor: themeColors.paper,
                borderRadius: 2,
                textAlign: 'center',
              }}
            >
              {/* Storage Error Icon */}
              <CloudOff
                sx={{
                  fontSize: 64,
                  color: errorDetails.severity === 'error' ? 'error.main' : 'warning.main',
                  mb: 2,
                }}
              />

              {/* Error Title */}
              <Typography
                variant="h5"
                component="h1"
                sx={{ mb: 2, color: themeColors.text }}
              >
                {errorDetails.title}
              </Typography>

              {/* Error Message */}
              <Alert severity={errorDetails.severity} sx={{ mb: 3, textAlign: 'left' }}>
                {errorDetails.message}
              </Alert>

              {/* Action Buttons */}
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mb: 2 }}>
                {errorDetails.isRetryable && (
                  <Button
                    variant="contained"
                    startIcon={<Refresh />}
                    onClick={this.handleRetry}
                    sx={{
                      bgcolor: themeColors.primary,
                      '&:hover': {
                        bgcolor: themeColors.accent,
                      },
                    }}
                  >
                    Try Again
                  </Button>
                )}

                {errorDetails.showConfigOption && onConfigureS3 && (
                  <Button
                    variant="outlined"
                    startIcon={<Settings />}
                    onClick={onConfigureS3}
                    sx={{
                      borderColor: themeColors.primary,
                      color: themeColors.primary,
                      '&:hover': {
                        borderColor: themeColors.accent,
                        bgcolor: 'rgba(65, 200, 196, 0.04)',
                      },
                    }}
                  >
                    Configure Storage
                  </Button>
                )}
              </Box>

              {/* Help Text */}
              <Typography variant="body2" sx={{ color: themeColors.textSecondary }}>
                {errorDetails.isRetryable 
                  ? 'This is usually a temporary issue. Please try again in a moment.'
                  : 'If this problem continues, please contact support for assistance.'
                }
              </Typography>
            </Paper>
          </Box>
        </Container>
      );
    }

    return children;
  }
}

export default S3ErrorBoundary;